import bbox from '@turf/bbox';
import area from '@turf/area';
import length from '@turf/length';
import type { ArmedInfrastructureTemplate, TemplateAttributeSchemaEntry } from './types';

export interface InfrastructureFinishField {
  key: string;
  label?: string;
  value: unknown;
  type?: string;
  unit?: string;
  enum?: string[];
  editable?: boolean;
}

interface GeometryMetrics {
  area_m2: number;
  area_ha: number;
  surface_area_ha: number;
  length_m: number;
  length_km: number;
  perimeter_m: number;
  span_m: number;
}

interface AutoFillContext {
  land_category?: string | null;
  linked_entity_id?: string | null;
}

function labelizeAttributeKey(key: string) {
  return String(key || '')
    .replace(/[_-]+/g, ' ')
    .replace(/\b\w/g, (character) => character.toUpperCase());
}

function bboxSpanMeters(bounds: [number, number, number, number] | null) {
  if (!bounds) return 0;
  const [west, south, east, north] = bounds;
  const centerLat = (south + north) / 2;
  const widthMeters = Math.abs(east - west) * 111320 * Math.max(Math.cos((centerLat * Math.PI) / 180), 0.2);
  const heightMeters = Math.abs(north - south) * 111320;
  return Math.max(widthMeters, heightMeters);
}

function computeGeometryMetrics(geometry: GeoJSON.Geometry): GeometryMetrics {
  const feature = { type: 'Feature' as const, geometry, properties: {} };
  const geometryType = geometry?.type;

  let areaM2 = 0;
  let lengthM = 0;
  let perimeterM = 0;
  let spanM = 0;

  try {
    if (geometryType === 'Polygon' || geometryType === 'MultiPolygon') {
      areaM2 = area(feature);
      perimeterM = length(feature, { units: 'meters' });
      lengthM = perimeterM;
    } else if (geometryType === 'LineString' || geometryType === 'MultiLineString') {
      lengthM = length(feature, { units: 'meters' });
      perimeterM = lengthM;
    }
    spanM = bboxSpanMeters(bbox(feature) as [number, number, number, number]);
  } catch {
    spanM = 0;
  }

  return {
    area_m2: areaM2,
    area_ha: areaM2 / 10000,
    surface_area_ha: areaM2 / 10000,
    length_m: lengthM,
    length_km: lengthM / 1000,
    perimeter_m: perimeterM,
    span_m: spanM,
  };
}

function resolveAutoFillValue(
  key: string,
  metrics: GeometryMetrics,
  context: AutoFillContext,
  autoFillSource?: string | null,
): unknown {
  const normalizedKey = String(autoFillSource || key || '').trim().toLowerCase();

  const metricAliases: Record<string, keyof GeometryMetrics> = {
    'geometry.area_m2': 'area_m2',
    'geometry.area_ha': 'area_ha',
    'geometry.surface_area_ha': 'surface_area_ha',
    'geometry.length_m': 'length_m',
    'geometry.length_km': 'length_km',
    'geometry.perimeter_m': 'perimeter_m',
    'geometry.span_m': 'span_m',
  };
  if (normalizedKey in metrics) {
    return metrics[normalizedKey as keyof GeometryMetrics];
  }
  if (metricAliases[normalizedKey]) {
    return metrics[metricAliases[normalizedKey]];
  }
  if (normalizedKey === 'linked_entity_id' || normalizedKey === 'context.linked_entity_id') {
    return context.linked_entity_id || undefined;
  }
  if (normalizedKey === 'land_category' || normalizedKey === 'context.land_category') {
    return context.land_category || undefined;
  }
  return undefined;
}

function normalizeField(
  attribute: TemplateAttributeSchemaEntry,
  metrics: GeometryMetrics,
  context: AutoFillContext,
): InfrastructureFinishField {
  const autoValue = resolveAutoFillValue(attribute.key, metrics, context, attribute.auto_fill_source);
  return {
    key: attribute.key,
    label: labelizeAttributeKey(attribute.key),
    value: autoValue !== undefined ? autoValue : (attribute.default_value ?? null),
    type: attribute.type,
    unit: attribute.unit,
    enum: attribute.enum,
    editable: autoValue === undefined,
  };
}

export function buildTemplateFinishFields(
  template: ArmedInfrastructureTemplate,
  geometry: GeoJSON.Geometry,
  context: AutoFillContext = {},
): InfrastructureFinishField[] {
  const metrics = computeGeometryMetrics(geometry);
  return (template.attribute_schema || []).map((attribute) => normalizeField(attribute, metrics, context));
}

export function buildTemplateAutoFilledAttributes(
  template: ArmedInfrastructureTemplate,
  geometry: GeoJSON.Geometry,
  context: AutoFillContext = {},
): Record<string, unknown> {
  const metrics = computeGeometryMetrics(geometry);
  const attributes: Record<string, unknown> = {};

  (template.attribute_schema || []).forEach((attribute) => {
    const autoValue = resolveAutoFillValue(attribute.key, metrics, context, attribute.auto_fill_source);
    attributes[attribute.key] = autoValue !== undefined ? autoValue : (attribute.default_value ?? null);
  });

  return attributes;
}
